import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Masked DD/MM/YYYY input that stores ISO YYYY-MM-DD internally.
 * Backwards-compatible: callers continue to pass and receive ISO strings.
 * - User types digits; slashes are inserted automatically.
 * - Calendar icon opens the native date picker (mobile-friendly).
 * - On blur, partial input is cleared so we never store an invalid date.
 */
export default function DateInput({
  value,                 // ISO YYYY-MM-DD
  onChange,              // (iso | "") => void
  testId,
  optional = false,
  min,                   // ISO YYYY-MM-DD
  max,                   // ISO YYYY-MM-DD
  className = "",
}) {
  const [display, setDisplay] = useState(isoToDisplay(value));
  const nativeRef = useRef(null);

  useEffect(() => {
    setDisplay(isoToDisplay(value));
  }, [value]);

  const handleChange = (e) => {
    const masked = maskDisplay(e.target.value);
    setDisplay(masked);
    const iso = displayToIso(masked);
    if (iso !== value) onChange(iso || "");
  };

  const handleBlur = () => {
    const iso = displayToIso(display);
    if (!iso && display) {
      setDisplay("");
      onChange("");
    }
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") return el.showPicker();
    } catch (e) {
      // Older browsers reject showPicker() in some contexts — fall back to focus/click.
      if (process.env.NODE_ENV === "development") console.debug("showPicker unavailable:", e);
    }
    el.focus(); el.click();
  };

  return (
    <div className={`relative flex items-center ${className}`}>
      <input
        type="text"
        inputMode="numeric"
        required={!optional}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="DD/MM/YYYY"
        maxLength={10}
        data-testid={testId}
        autoComplete="off"
        className="w-full pr-9 border border-[#e2e8f0] bg-white text-[#0f1b2d] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition"
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Open calendar"
        data-testid={testId ? `${testId}-calendar-btn` : undefined}
        className="absolute right-2 text-[#475569] hover:text-[#2563eb] p-1"
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={nativeRef}
        type="date"
        value={value || ""}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value || "")}
        tabIndex={-1}
        aria-hidden="true"
        data-testid={testId ? `${testId}-native` : undefined}
        className="absolute opacity-0 pointer-events-none w-0 h-0"
      />
    </div>
  );
}

/* ---------- helpers ---------- */
export function isoToDisplay(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function displayToIso(s) {
  if (!s) return "";
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return "";
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return "";
  if (dd < 1 || dd > 31) return "";
  if (yyyy < 1900 || yyyy > 2100) return "";
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (date.getUTCFullYear() !== yyyy || date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function maskDisplay(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  const a = digits.slice(0, 2);
  const b = digits.slice(2, 4);
  const c = digits.slice(4, 8);
  let out = a;
  if (digits.length >= 3) out += "/" + b;
  if (digits.length >= 5) out += "/" + c;
  return out;
}
